import React, { useState } from "react";
import "./LookSelector.css";
import { LookInfo } from "../../handlers/types";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { getLookUpgradeDescription } from "../../utils/lookUtils";

interface LookSelectorProps {
  look: LookInfo;
  isSelected?: boolean;
  onSelect: (look: LookInfo) => void;
  onPurchase: (look: LookInfo) => Promise<void>;
}

export const LookSelector: React.FC<LookSelectorProps> = ({
  look,
  isSelected = false,
  onSelect,
  onPurchase,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n();

  const handleClick = async () => {
    if (look.isOwned) {
      onSelect(look);
    } else if (!isLoading) {
      setIsLoading(true);
      try {
        await onPurchase(look);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Get the primary upgrade description if any
  const getUpgradeDescription = () => {
    return getLookUpgradeDescription(look, t);
  };

  const renderContent = () => {
    return (
      <>
        {look.rarity && (
          <div
            className={`look-selector__rarity look-selector__rarity--${look.rarity.toLowerCase()}`}
          >
            {t(`looks.rarity.${look.rarity.toLowerCase()}`, {
              defaultValue: look.rarity,
            })}
          </div>
        )}
        <div className="look-selector__sprite">
          <div
            className="look-selector__sprite-frame"
            style={{
              background: `url(/assets/img/characters/${look.spriteKey}.webp) -60px -8px`,
            }}
            aria-label={t("looks.character", {
              defaultValue: "Character: {{name}}",
              name: look.nameKey,
            })}
            role="img"
          />
        </div>
        {getUpgradeDescription() && (
          <div className="look-selector__upgrade-desc">
            {getUpgradeDescription()}
          </div>
        )}
      </>
    );
  };

  const renderPrice = () => {
    if (!look.price) return null;

    if (look.discountPrice) {
      return (
        <div className="look-selector__price">
          <div className="look-selector__price-container">
            <span className="look-selector__price-value look-selector__price-value--original">
              {look.price}
            </span>
            &nbsp;
            <span className="look-selector__price-value look-selector__price-value--discount">
              {look.discountPrice}
            </span>
            <span className="look-selector__star" aria-hidden="true" />
          </div>
        </div>
      );
    }

    return (
      <div className="look-selector__price">
        <span className="look-selector__price-value">{look.price}</span>
        <span className="look-selector__star" aria-hidden="true" />
      </div>
    );
  };

  const renderOwned = () => {
    return (
      <div className="look-selector__owned">
        {t("looks.owned", { defaultValue: "Owned" })}
      </div>
    );
  };

  return (
    <div
      className={`look-selector ${look.isOwned ? "look-selector--owned" : ""} ${
        isSelected ? "look-selector--selected" : ""
      } ${isLoading ? "look-selector--loading" : ""}`}
      onClick={handleClick}
    >
      {renderContent()}
      {look.isOwned && renderOwned()}
      {!look.isOwned && look.price && renderPrice()}
    </div>
  );
};
